'use client';

import { useState } from 'react';

interface UseZIndexManagerOptions {
  wrapAround?: number;
}

export function useZIndexManager(options: UseZIndexManagerOptions = {}) {
  const { wrapAround = 50 } = options;
  const [zIndexCounter, setZIndexCounter] = useState(0); // Stores current highest z-index
  const [zIndexMap, setZIndexMap] = useState<Record<string, number>>({});

  /**
   * Returns the current z-index for an element, falling back to 0
   * if it has not been clicked. Use this instead of reading zIndexMap directly.
   */
  const getZIndex = (id: string) => zIndexMap[id] ?? 0;

  const handleElementClick = (id: string) => {
    const zIndex = zIndexCounter + 1;
    
    if (zIndex < wrapAround) {
      setZIndexMap(prev => ({ ...prev, [id]: zIndex }));
      setZIndexCounter(zIndex);
    } 
    else {
      // console.log('wrapping around - compacting z-indexes')
      // Compact: sort elements by current z-index, reassign 1, 2, 3... preserving order
      setZIndexMap(prev => {
        const entries = Object.entries(prev).sort(([, a], [, b]) => a - b);
        const compacted: Record<string, number> = {};
        entries.forEach(([key, _prevIndex], index) => {
          compacted[key] = index + 1;
        });
        // Add the clicked element at the top
        compacted[id] = entries.length + 1;
        return compacted;
      });
      setZIndexCounter(Object.keys(zIndexMap).length + 1);
      // console.log(`New counter: ${Object.keys(zIndexMap).length + 1}`)
    }
  };

  const resetZIndexMap = () => {
    setZIndexMap({});
    setZIndexCounter(0);
  };

  return {
    getZIndex,
    handleElementClick,
    resetZIndexMap,
  };
}